import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCreditNote } from '../../services/api';
import { storage } from '../../utils/storage';

const CreateCreditNoteModal = ({ invoice, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [isPartial, setIsPartial] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const restaurantId = storage.get('selectedRestaurantId');

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('invoicing.reasonRequired', 'El motivo es requerido'));
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const data = {
        restaurantId,
        originalInvoiceId: invoice.invoiceId,
        reason: reason.trim(),
        amount: isPartial ? parseFloat(amount) : null,
      };
      const res = await createCreditNote(data);
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.afipError ||
          err.response?.data?.error ||
          t('invoicing.creditNoteError', 'Error al crear nota de crédito')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('invoicing.createCreditNote', 'Crear Nota de Crédito')}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="invoice-error" role="alert" aria-live="assertive">
              <span className="material-icons">error</span>
              {error}
            </div>
          )}

          {result ? (
            <div className="invoice-result" role="status" aria-live="polite">
              <div className="invoice-result-icon">
                <span className="material-icons">check_circle</span>
              </div>
              <h3>{t('invoicing.creditNoteCreated', 'Nota de crédito emitida')}</h3>
              <div className="invoice-result-details">
                <div className="result-row">
                  <span>NC {result.invoiceClass}:</span>
                  <span className="mono">
                    {String(result.puntoDeVenta).padStart(5, '0')}-
                    {String(result.numeroComprobante).padStart(8, '0')}
                  </span>
                </div>
                <div className="result-row">
                  <span>CAE:</span>
                  <span className="mono">{result.cae}</span>
                </div>
                <div className="result-row">
                  <span>Importe:</span>
                  <span>${(result.importeTotal || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="invoice-detail-related" style={{ marginBottom: 'var(--space-md)' }}>
                <span className="material-icons">receipt_long</span>
                {t('invoicing.originalInvoice', 'Factura original')}: {invoice.invoiceClass}{' '}
                {String(invoice.puntoDeVenta).padStart(5, '0')}-
                {String(invoice.numeroComprobante).padStart(8, '0')} — $
                {(invoice.importeTotal || 0).toFixed(2)}
              </div>

              <div className="invoice-form-group">
                <label>{t('invoicing.reason', 'Motivo')}</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Devolución, error en facturación..."
                />
              </div>

              <div className="invoice-form-group">
                <label className="invoice-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPartial}
                    onChange={(e) => setIsPartial(e.target.checked)}
                  />
                  {t('invoicing.partialAmount', 'Monto parcial')}
                </label>
                {isPartial && (
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Máximo: $${(invoice.importeTotal || 0).toFixed(2)}`}
                    min={0.01}
                    max={invoice.importeTotal}
                    step={0.01}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-actions">
          {result ? (
            <button type="button" className="btn btn-primary" onClick={onCreated}>
              {t('common.close', 'Cerrar')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? t('invoicing.creating', 'Emitiendo...')
                  : t('invoicing.emitCreditNote', 'Emitir Nota de Crédito')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCreditNoteModal;
